import { Link } from 'react-router-dom';
import { Factory, Users, Zap, TrendingUp, CheckCircle, Award } from 'lucide-react';

const Home = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_pslcote-labels/artifacts/bhn9rzyp_Homepage%20banner.png" 
            alt="Label Manufacturing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/40 to-black/30"></div>
        </div>
        <div className="section-container relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg" data-testid="hero-title">
              Diversified Custom Label Solutions
            </h1>
            <p className="text-lg lg:text-xl text-white mb-8 leading-relaxed drop-shadow-md">
              Custom-engineered label stock solutions aligned to brand identity, operational needs, and performance requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products">
                <button className="bg-[#0066cc] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#0052a3] transition-colors shadow-lg" data-testid="explore-products-btn">
                  Explore Our Products
                </button>
              </Link>
              <Link to="/contact">
                <button className="bg-white text-[#0066cc] px-8 py-3 rounded-lg font-semibold border-2 border-white hover:bg-transparent hover:text-white transition-all shadow-lg" data-testid="get-quote-btn">
                  Request a Consultation
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="who-we-are-title">Who We Are</h2>
            <p className="section-subtitle mx-auto">
              We meticulously assess each customer's unique requirements to deliver custom-engineered label stock solutions that ensure superior performance, consistency, and competitive edge.
            </p>
          </div>
        </div>
      </section>

      {/* Customer Partnership Model */}
      <section className="bg-gray-50 py-16 lg:py-24">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="partnership-model-title">Customer Partnership Model</h2>
            <p className="section-subtitle mx-auto mb-8">
              Complete responsive solution with dynamic flexibility and superior quality
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 card-hover" data-testid="material-card">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Factory className="feature-icon" />
              </div>
              <h3 className="text-2xl font-bold text-center mb-4 pslcote-blue">Material</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                30+ label stock variants with diverse face materials, adhesives, and release liners
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 card-hover" data-testid="manpower-card">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Users className="feature-icon" />
              </div>
              <h3 className="text-2xl font-bold text-center mb-4 pslcote-blue">Manpower</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Expert technical team with deep industry knowledge and R&D capabilities
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 card-hover" data-testid="machine-card">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Zap className="feature-icon" />
              </div>
              <h3 className="text-2xl font-bold text-center mb-4 pslcote-blue">Machine</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                State-of-the-art coating and slitting machinery for precision manufacturing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* In-House Manufacturing */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="section-title" data-testid="manufacturing-title">In-House Manufacturing Advantage</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <CheckCircle className="text-[#0066cc] flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold text-lg mb-2">Backward Integration</h4>
                <p className="text-gray-600 text-sm">Complete control over quality and consistency</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <CheckCircle className="text-[#0066cc] flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold text-lg mb-2">Cost Optimization</h4>
                <p className="text-gray-600 text-sm">Efficient production reducing overall costs</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <CheckCircle className="text-[#0066cc] flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold text-lg mb-2">No Wet Glue Required</h4>
                <p className="text-gray-600 text-sm">Pressure-sensitive adhesive technology</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <CheckCircle className="text-[#0066cc] flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold text-lg mb-2">Easy Application</h4>
                <p className="text-gray-600 text-sm">Compatible with automated labeling systems</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <CheckCircle className="text-[#0066cc] flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold text-lg mb-2">Versatile Usage</h4>
                <p className="text-gray-600 text-sm">Roll form labels for diverse applications</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <CheckCircle className="text-[#0066cc] flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold text-lg mb-2">3" Core Compatible</h4>
                <p className="text-gray-600 text-sm">Standard industrial label machine compatibility</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Production Capacity */}
      <section className="bg-gradient-to-br from-[#0066cc] to-[#0052a3] py-16 lg:py-24 text-white">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" data-testid="capacity-title">
              50 Lakh Sq. Meter
            </h2>
            <p className="text-xl lg:text-2xl opacity-90">Monthly Production Capacity</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-12">
            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <TrendingUp className="mx-auto mb-4" size={40} />
                <h4 className="font-semibold text-lg mb-2">Rapid Market Response</h4>
                <p className="text-sm opacity-90">Swift production scaling to meet urgent demands</p>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Award className="mx-auto mb-4" size={40} />
                <h4 className="font-semibold text-lg mb-2">Growth Opportunity</h4>
                <p className="text-sm opacity-90">Significant expansion headroom without new capex</p>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Zap className="mx-auto mb-4" size={40} />
                <h4 className="font-semibold text-lg mb-2">Production Flexibility</h4>
                <p className="text-sm opacity-90">Diverse requirements handled simultaneously</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <button className="bg-white text-[#0066cc] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors" data-testid="explore-cta-btn">
                Explore Our Product Categories
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;